import PaginatedComponent from 'core/PaginatedComponent';
import { createCustomSlice } from 'core/utils';
import i18n from 'i18nextInstance';
import moment from 'moment';

const initialState = {
    absenceRate: [],
    absenceRateMonthly: [],
    absenceReports: {},
    absenceReportsDownload: [],
    absenceReportsDownloading: false,
    absenceReportsPage: null,
    absenceReportsPageLoading: false,
    absencesActive: [],
    absencesByLicense: [],
    absencesByLicenseMonthly: [],
    currentFilters: {},
    diagnosis: [],
    diagnosisGrouped: [],
    general: {
        appointments: {},
        bills: {},
        consultations: {},
        patients: {},
        professionals: {},
        videocalls: {}
    },
    healthSurveys: {},
    healthSurveysDownload: [],
    healthSurveysDownloading: false,
    licenses: [],
    licensesActive: [],
    licensesDownload: [],
    licensesDownloading: false,
    licensesGranted: [],
    licensesHealthIndex: {},
    licensesSummary: {},
    loading: false,
    patient: null,
    patientsAttended: {},
    patientsAttendedDownload: [],
    patientsAttendedDownloading: false,
    preworks: {},
    preworksDownload: [],
    preworksDownloading: false,
    professionals: null,
    professionalsDownload: null,
    professionalsDownloading: false,
    psychosocialRisk: {},
    psychosocialRiskDownload: {},
    psychosocialRiskDownloading: false,
    quizzes: {},
    quizzesDownload: {},
    quizzesDownloading: false,
    recipeRequests: {},
    recipeRequestsDownload: [],
    recipeRequestsDownloading: false,
    recommendations: [],
    riskFactors: [],
    riskFactorsDownload: [],
    riskFactorsDownloading: false,
    riskFactorsPage: null,
    riskFactorsPageLoading: false,
    sessions: null,
    sessionsDownload: null,
    sessionsDownloading: false,
    surveys: {},
    surveysDownload: [],
    surveysDownloading: false,
    symptoms: [],
    turns: {},
    turnsDownload: [],
    turnsDownloading: false,
    updating: false,
    videocall: {
        diagnoses: [],
        recommendations: [],
        symptom: []
    }
};

const statisticsSlice = createCustomSlice({
    initialState,
    name: 'statistics',
    reducers: {
        setStatisticsAbsenceReportsPage (state, { payload }) {
            state.absenceReportsPage = new PaginatedComponent({
                ...payload,
                loadingAction: statisticsActions.setStatisticsAbsenceReportsPageLoading,
                setAction: statisticsActions.setStatisticsAbsenceReportsPage
            });
        },
        setStatisticsRiskFactorsPage (state, { payload }) {
            state.riskFactorsPage = new PaginatedComponent({
                ...payload,
                loadingAction: statisticsActions.setStatisticsRiskFactorsPageLoading,
                setAction: statisticsActions.setStatisticsRiskFactorsPage
            });
        }
    },
    saga: [
        'getStatisticsAbsenceRate',
        'getStatisticsAbsenceReports',
        'getStatisticsAbsenceReportsDownload',
        'getStatisticsAbsenceReportsPage',
        'getStatisticsAbsencesActive',
        'getStatisticsAbsencesByLicense',
        'getStatisticsAbsencesByLicenseMonthly',
        'getStatisticsDiagnosis',
        'getStatisticsDiagnosisGrouped',
        'getStatisticsGeneral',
        'getStatisticsHealthSurveys',
        'getStatisticsHealthSurveysDownload',
        'getStatisticsLicenses',
        'getStatisticsLicensesActive',
        'getStatisticsLicensesDownload',
        'getStatisticsLicensesGranted',
        'getStatisticsLicensesHealthIndex',
        'getStatisticsPatientsAttended',
        'getStatisticsPatientsAttendedDownload',
        'getStatisticsPreworks',
        'getStatisticsPreworksDownload',
        'getStatisticsProfessionals',
        'getStatisticsProfessionalsDownload',
        'getStatisticsPsychosocialRisk',
        'getStatisticsPsychosocialRiskDownload',
        'getStatisticsQuizzes',
        'getStatisticsQuizzesDownload',
        'getStatisticsRecipeRequests',
        'getStatisticsRecipeRequestsDownload',
        'getStatisticsRecommendations',
        'getStatisticsRiskFactors',
        'getStatisticsRiskFactorsDownload',
        'getStatisticsRiskFactorsPage',
        'getStatisticsSessions',
        'getStatisticsSessionsDownload',
        'getStatisticsSurveys',
        'getStatisticsSurveysDownload',
        'getStatisticsSymptoms',
        'getStatisticsTurns',
        'getStatisticsTurnsDownload',
        'getStatisticsVideocall'
    ]
});

export const parseCallType = callType => {
    switch (callType) {
    case 'appointment': return i18n.t('Turno');
    case 'evolution': return i18n.t('Evolución');
    case 'interconsut': return i18n.t('Interconsulta');
    case 'ondemand': return i18n.t('Demanda espontánea');
    case 'pin': return i18n.t('PIN');
    default: return i18n.t('No disponible');
    }
};

export const parseLicenseAttentionType = value => {
    switch (value) {
    case ('certificate'): return i18n.t('Certificado');
    case ('external'): return i18n.t('Externa');
    case ('office'): return i18n.t('Consultorio');
    case ('visit'): return i18n.t('A domicilio');
    default: return '-';
    }
};

export const parsePaymentMethod = method => {
    switch (method) {
    case 'CASH': return i18n.t('Efectivo');
    case 'COVERAGE': return i18n.t('Cobertura');
    case 'ONLINE_CANCELLED': return `${ i18n.t('Online') } - ${ i18n.t('Cancelado') }`;
    case 'ONLINE_MP': return i18n.t('MercadoPago');
    case 'ONLINE_PENDING': return `${ i18n.t('Online') } - ${ i18n.t('Pendiente') }`;
    case 'ONLINE_STRIPE': return i18n.t('Stripe');
    default: return i18n.t('Ninguno');
    }
};

export const parsePsychosocialRiskAnswer = answer => {
    const mapper = {
        almost_always: i18n.t('Casi siempre'),
        almost_never: i18n.t('Casi nunca'),
        always: i18n.t('Siempre'),
        never: i18n.t('Nunca'),
        no: i18n.t('No'),
        sometimes: i18n.t('A veces'),
        yes: i18n.t('Si')
    };
    return mapper[answer] ?? '';
};

export const parsePsychosocialRiskResult = result => {
    const mapper = {
        high: i18n.t('Alto'),
        low: i18n.t('Bajo'),
        medium: i18n.t('Medio'),
        none: i18n.t('Nulo o despreciable'),
        very_high: i18n.t('Muy alto')
    };
    return mapper[result] ?? '';
};

export const parseStatisticsAbsencesByLicenseMonthly = data => data.map(elem => {
    const { month } = elem;
    return {
        ...elem,
        key: month,
        label: moment().set('day', 1).set('month', month - 1).format('MMM')
    };
});

export const statisticsActions = statisticsSlice.actions;

export default statisticsSlice.reducer;
